import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const SupplierEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [companyname, setCompanyName] = useState("");
  const [contactperson, setcontactperson] = useState("");
  const [contactno, setcontactno] = useState("");
  const [gstno, setGstNo] = useState("");
  const [udyamnumber, setUdyamNumber] = useState("");
  const [udyamtype, setUdyamType] = useState(null);
  const [email, setEmail] = useState("");
  const [packingandforwarding, setPackingAndForwarding] = useState("");
  const [paymentterm, setPaymentTerm] = useState('');
  const [freight, setFreight] = useState("");
  const [incoterms, setIncoterms] = useState('');
  const [addresses, setAddresses] = useState([{
    supplieraddress: "",
    country: null,
    state: null,
    city: null,
    stateOptions: [],
    cityOptions: [],
    statecode: "",
    pincode: "",
  }]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accountno, setaccountno] = useState("");
  const [bankname, setbankname] = useState("");
  const [bankbranchname, setbankbranchname] = useState("");
  const [ifsccode, setifsccode] = useState("");

  const udyamTypeOptions = [
    { value: 'Micro', label: 'Micro' },
    { value: 'Small', label: 'Small' },
    { value: 'Medium', label: 'Medium' }
  ];

  // Enhanced validation states
  const [errors, setErrors] = useState({
    companyname: "",
    contactperson: "",
    contactno: "",
    email: "",
    gstno: "",
    udyamnumber: "",
    accountno: "",
    bankname: "",
    bankbranchname: "",
    ifsccode: "",
    addresses: []
  });

  const [touched, setTouched] = useState({
    companyname: false,
    contactperson: false,
    contactno: false,
    email: false,
    gstno: false,
    udyamnumber: false,
    accountno: false,
    bankname: false,
    bankbranchname: false,
    ifsccode: false,
    addresses: []
  });

  // Capitalization functions
  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${BASE_URL}country/findAllCountry`, {
          headers: authHeader(),
        });
        setCountryOptions(response.data.map(country => ({ value: country.id, label: country.countryname })));
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch countries. Please try again.',
        });
      }
    };
    fetchCountries();
  }, []);

  // Fetch supplier details by ID
  useEffect(() => {
    const fetchSupplier = async () => {
      if (id) {
        try {
          const response = await axios.get(`${BASE_URL}supplier/findById/${id}`, {
            headers: authHeader(),
          });
          const supplierData = response.data;

          setaccountno(supplierData.accountno || "");
          setbankname(supplierData.bankname || "");
          setbankbranchname(supplierData.bankbranchname || "");
          setifsccode(supplierData.ifsccode || "");

          setCompanyName(supplierData.companyname || "");
          setcontactperson(supplierData.contactperson || "");
          setcontactno(supplierData.contactno || "");
          setGstNo(supplierData.gstno || "");
          setUdyamNumber(supplierData.udyamnumber || "");
          setUdyamType(supplierData.udyamtype ? { value: supplierData.udyamtype, label: supplierData.udyamtype } : null);
          setEmail(supplierData.email || "");
          setPackingAndForwarding(supplierData.packingandforwarding || "");
          setPaymentTerm(supplierData.paymentterm || "");
          setFreight(supplierData.freight || "");
          setIncoterms(supplierData.incoterms || "");

          const supplierAddresses = supplierData.supplierMultipleAddress?.map(addr => ({
            supplieraddress: addr.supplieraddress || "",
            country: addr.country ? { value: addr.country.id, label: addr.country.countryname } : null,
            state: addr.state ? { value: addr.state.id, label: addr.state.statename, code: addr.state.statecode } : null,
            city: addr.city ? { value: addr.city.id, label: addr.city.cityname } : null,
            stateOptions: [],
            cityOptions: [],
            statecode: addr.statecode || "",
            pincode: addr.pincode || "",
          })) || [{
            supplieraddress: "",
            country: null,
            state: null,
            city: null,
            stateOptions: [],
            cityOptions: [],
            statecode: "",
            pincode: "",
          }];

          // Fetch state options for each address with country
          for (let i = 0; i < supplierAddresses.length; i++) {
            if (supplierAddresses[i].country) {
              try {
                const statesRes = await axios.get(`${BASE_URL}state/stateByCountry/${supplierAddresses[i].country.value}`, {
                  headers: authHeader(),
                });
                supplierAddresses[i].stateOptions = statesRes.data.map(state => ({
                  value: state.id,
                  label: state.statename,
                  code: state.statecode,
                }));
              } catch (error) {
                console.error("Failed to fetch states:", error);
              }
            }

            if (supplierAddresses[i].state) {
              try {
                const citiesRes = await axios.get(`${BASE_URL}city/cityByStateId/${supplierAddresses[i].state.value}`, {
                  headers: authHeader(),
                });
                supplierAddresses[i].cityOptions = citiesRes.data.map(city => ({
                  value: city.id,
                  label: city.cityname,
                }));
              } catch (error) {
                console.error("Failed to fetch cities:", error);
              }
            }
          }

          setAddresses(supplierAddresses);
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch supplier details. Please try again.',
          });
        }
      }
    };

    fetchSupplier();
  }, [id]);

  // Enhanced validation function
  const validateField = (name, value, addressIndex = null) => {
    let error = "";

    switch (name) {
      case "companyname":
        if (!value || !value.trim()) error = "Company Name is required";
        else if (value.trim().length < 3) error = "Company Name must be at least 3 characters";
        else if (value.trim().length > 100) error = "Company Name must be less than 100 characters";
        break;

      case "contactperson":
        if (!value || !value.trim()) error = "Contact Person is required";
        else if (value.trim().length < 3) error = "Contact Person must be at least 3 characters";
        else if (value.trim().length > 100) error = "Contact Person must be less than 100 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = "Contact Person should contain only letters and spaces";
        break;

      case "contactno":
        if (value && value.trim() && !/^[6-9]\d{9}$/.test(value))
          error = "Please enter a valid 10-digit mobile number starting with 6-9";
        break;

      case "email":
        if (!value || !value.trim()) {
          error = "Email is required";
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "gstno":
        if (value && value.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/.test(value))
          error = "Please enter a valid GST number (Format: 22AAAAA0000A1Z5)";
        break;

      case "udyamnumber":
        if (value && value.trim() && !/^[A-Z0-9]{11,20}$/.test(value))
          error = "Please enter a valid Udyam number";
        break;

      case "accountno":
        if (value && value.trim()) {
          if (!/^[0-9]+$/.test(value)) error = "Account number should contain only digits";
          else if (value.trim().length < 8) error = "Account number must be at least 8 digits";
          else if (value.trim().length > 18) error = "Account number must be less than 18 digits";
        }
        break;

      case "bankname":
        if (value && value.trim() && value.trim().length < 2) error = "Bank name must be at least 2 characters";
        break;

      case "bankbranchname":
        if (value && value.trim() && value.trim().length < 2) error = "Bank branch name must be at least 2 characters";
        break;

      case "ifsccode":
        if (value && value.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase()))
          error = "Please enter a valid IFSC code (Format: SBIN0123456)";
        break;

      case "supplieraddress":
        if (value && value.trim() && value.trim().length < 5) error = "Address must be at least 5 characters if provided";
        break;

      case "pincode":
        if (value && value.trim() && !/^[1-9][0-9]{5}$/.test(value))
          error = "Please enter a valid 6-digit pincode";
        break;

      case "country":
        if (!value) error = "Country is required";
        break;

      case "state":
        if (!value) error = "State is required";
        break;

      case "city":
        if (!value) error = "City is required";
        break;

      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    switch (name) {
      case "companyname":
        processedValue = capitalizeAllWords(value);
        setCompanyName(processedValue);
        break;
      case "contactperson":
        processedValue = capitalizeAllWords(value);
        setcontactperson(processedValue);
        break;
      case "contactno":
        processedValue = value.replace(/\D/g, '').slice(0, 10);
        setcontactno(processedValue);
        break;
      case "email":
        processedValue = value.toLowerCase();
        setEmail(processedValue);
        break;
      case "gstno":
        processedValue = value.toUpperCase().slice(0, 15);
        setGstNo(processedValue);
        break;
      case "udyamnumber":
        processedValue = value.toUpperCase().slice(0, 20);
        setUdyamNumber(processedValue);
        break;
      case "paymentterm":
        processedValue = capitalizeAllWords(value);
        setPaymentTerm(processedValue);
        break;
      case "incoterms":
        processedValue = value.toUpperCase();
        setIncoterms(processedValue);
        break;
      case "bankname":
        processedValue = capitalizeAllWords(value);
        setbankname(processedValue);
        break;
      case "bankbranchname":
        processedValue = capitalizeAllWords(value);
        setbankbranchname(processedValue);
        break;
      case "ifsccode":
        processedValue = value.toUpperCase().slice(0, 11);
        setifsccode(processedValue);
        break;
      case "accountno":
        processedValue = value.replace(/\D/g, '').slice(0, 18);
        setaccountno(processedValue);
        break;
      default:
        break;
    }

    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleCountryChange = async (index, selectedOption) => {
    const updatedAddresses = [...addresses];
    updatedAddresses[index].country = selectedOption;
    updatedAddresses[index].state = null;
    updatedAddresses[index].city = null;
    updatedAddresses[index].stateOptions = [];
    updatedAddresses[index].cityOptions = [];
    updatedAddresses[index].statecode = "";

    const updatedTouched = { ...touched };
    if (!updatedTouched.addresses) updatedTouched.addresses = [];
    updatedTouched.addresses[index] = { ...updatedTouched.addresses[index], country: true };
    setTouched(updatedTouched);

    const error = validateField("country", selectedOption);
    const updatedErrors = { ...errors };
    if (!updatedErrors.addresses) updatedErrors.addresses = [];
    updatedErrors.addresses[index] = { ...updatedErrors.addresses[index], country: error };
    setErrors(updatedErrors);

    if (!selectedOption) {
      setAddresses(updatedAddresses);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}state/stateByCountry/${selectedOption.value}`, {
        headers: authHeader(),
      });
      updatedAddresses[index].stateOptions = response.data.map(state => ({
        value: state.id,
        label: state.statename,
        code: state.statecode,
      }));
      setAddresses(updatedAddresses);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch states. Please try again.',
      });
    }
  };

  const handleStateChange = async (index, selectedOption) => {
    const updatedAddresses = [...addresses];
    updatedAddresses[index].state = selectedOption;
    updatedAddresses[index].city = null;
    updatedAddresses[index].statecode = selectedOption?.code || "";

    const updatedTouched = { ...touched };
    if (!updatedTouched.addresses) updatedTouched.addresses = [];
    updatedTouched.addresses[index] = { ...updatedTouched.addresses[index], state: true };
    setTouched(updatedTouched);

    const error = validateField("state", selectedOption);
    const updatedErrors = { ...errors };
    if (!updatedErrors.addresses) updatedErrors.addresses = [];
    updatedErrors.addresses[index] = { ...updatedErrors.addresses[index], state: error };
    setErrors(updatedErrors);

    if (!selectedOption) {
      setAddresses(updatedAddresses);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}city/cityByStateId/${selectedOption.value}`, {
        headers: authHeader(),
      });
      updatedAddresses[index].cityOptions = response.data.map(city => ({ value: city.id, label: city.cityname }));
      setAddresses(updatedAddresses);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch cities. Please try again.',
      });
    }
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...addresses];
    let processedValue = value;

    if (field === "supplieraddress") {
      processedValue = capitalizeAllWords(value);
    } else if (field === "pincode") {
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    } else if (field === "city") {
      newAddresses[index].city = value;

      const updatedTouched = { ...touched };
      if (!updatedTouched.addresses) updatedTouched.addresses = [];
      updatedTouched.addresses[index] = { ...updatedTouched.addresses[index], city: true };
      setTouched(updatedTouched);

      const error = validateField("city", value);
      const updatedErrors = { ...errors };
      if (!updatedErrors.addresses) updatedErrors.addresses = [];
      updatedErrors.addresses[index] = { ...updatedErrors.addresses[index], city: error };
      setErrors(updatedErrors);

      setAddresses(newAddresses);
      return;
    }

    newAddresses[index][field] = processedValue;

    if (field === "supplieraddress" || field === "pincode") {
      const updatedTouched = { ...touched };
      if (!updatedTouched.addresses) updatedTouched.addresses = [];
      updatedTouched.addresses[index] = { ...updatedTouched.addresses[index], [field]: true };
      setTouched(updatedTouched);

      const error = validateField(field, processedValue);
      const updatedErrors = { ...errors };
      if (!updatedErrors.addresses) updatedErrors.addresses = [];
      updatedErrors.addresses[index] = { ...updatedErrors.addresses[index], [field]: error };
      setErrors(updatedErrors);
    }

    setAddresses(newAddresses);
  };

  const addAddress = () => {
    setAddresses([...addresses, {
      supplieraddress: "",
      country: null,
      state: null,
      city: null,
      stateOptions: [],
      cityOptions: [],
      statecode: "",
      pincode: "",
    }]);
  };

  const removeAddress = (index) => {
    if (addresses.length > 1) {
      const newAddresses = addresses.filter((_, i) => i !== index);
      setAddresses(newAddresses);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Cannot Remove",
        text: "At least one address is required",
      });
    }
  };

  // Improved validateAddresses function with detailed error collection
  const validateAddresses = () => {
    let isValid = true;
    const addressErrors = [];
    const errorFields = [];

    addresses.forEach((addr, idx) => {
      const addrErrors = {};

      // Validate country
      const countryError = validateField("country", addr.country);
      if (countryError) {
        addrErrors.country = countryError;
        errorFields.push(`Address ${idx + 1} - Country`);
        isValid = false;
      }

      // Validate state
      const stateError = validateField("state", addr.state);
      if (stateError) {
        addrErrors.state = stateError;
        errorFields.push(`Address ${idx + 1} - State`);
        isValid = false;
      }

      // Validate city
      const cityError = validateField("city", addr.city);
      if (cityError) {
        addrErrors.city = cityError;
        errorFields.push(`Address ${idx + 1} - City`);
        isValid = false;
      }

      // Validate pincode if provided
      if (addr.pincode) {
        const pincodeError = validateField("pincode", addr.pincode);
        if (pincodeError) {
          addrErrors.pincode = pincodeError;
          errorFields.push(`Address ${idx + 1} - Pincode`);
          isValid = false;
        }
      }

      // Validate address if provided
      if (addr.supplieraddress) {
        const addressError = validateField("supplieraddress", addr.supplieraddress);
        if (addressError) {
          addrErrors.supplieraddress = addressError;
          errorFields.push(`Address ${idx + 1} - Address Line`);
          isValid = false;
        }
      }

      addressErrors.push(addrErrors);
    });

    setErrors(prev => ({ ...prev, addresses: addressErrors }));

    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Address Validation Error',
        html: `<div style="text-align: left;">
          <strong>Please fix the following address errors:</strong><br/><br/>
          ${errorFields.map(field => `• ${field}`).join('<br/>')}
        </div>`,
        confirmButtonText: 'OK'
      });
    }

    return isValid;
  };

  // Improved checkRequiredFields function with detailed error collection
  const checkRequiredFields = () => {
    let isValid = true;
    const missingFields = [];

    const requiredFields = [
      { name: 'companyname', value: companyname, label: 'Company Name' },
      { name: 'contactperson', value: contactperson, label: 'Contact Person' },
      { name: 'email', value: email, label: 'Email Id' }
    ];

    requiredFields.forEach(field => {
      if (!field.value || !field.value.trim()) {
        missingFields.push(field.label);
        isValid = false;
      }
    });

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Fields',
        html: `<div style="text-align: left;">
          <strong>Please fill in the following required fields:</strong><br/><br/>
          ${missingFields.map(field => `• ${field}`).join('<br/>')}
        </div>`,
        confirmButtonText: 'OK'
      });
    }

    // Check if at least one address has required fields
    let hasValidAddress = false;
    addresses.forEach(addr => {
      if (addr.country && addr.state && addr.city) {
        hasValidAddress = true;
      }
    });

    if (!hasValidAddress && addresses.length > 0) {
      isValid = false;
      Swal.fire({
        icon: 'warning',
        title: 'Address Required',
        text: 'Please fill country, state and city for at least one address',
      });
    }

    return isValid;
  };

  // Improved validateAllFields function
  const validateAllFields = () => {
    const newErrors = {};
    const errorFields = [];
    let isValid = true;

    // Required field validations
    const requiredFields = [
      { name: "companyname", value: companyname, label: "Company Name" },
      { name: "contactperson", value: contactperson, label: "Contact Person" },
      { name: "email", value: email, label: "Email" }
    ];

    for (const field of requiredFields) {
      const error = validateField(field.name, field.value);
      if (error) {
        newErrors[field.name] = error;
        errorFields.push(field.label);
        isValid = false;
      }
    }

    // Optional field validations
    const optionalFields = [
      { name: "contactno", value: contactno, label: "Mobile Number" },
      { name: "gstno", value: gstno, label: "GST Number" },
      { name: "udyamnumber", value: udyamnumber, label: "Udyam Number" },
      { name: "accountno", value: accountno, label: "Account Number" },
      { name: "bankname", value: bankname, label: "Bank Name" },
      { name: "bankbranchname", value: bankbranchname, label: "Bank Branch Name" },
      { name: "ifsccode", value: ifsccode, label: "IFSC Code" }
    ];

    for (const field of optionalFields) {
      if (field.value && field.value.trim()) {
        const error = validateField(field.name, field.value);
        if (error) {
          newErrors[field.name] = error;
          errorFields.push(field.label);
          isValid = false;
        }
      }
    }

    setErrors(prev => ({ ...prev, ...newErrors }));

    // Show detailed error message
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: `<div style="text-align: left;">
          <strong>Please fix the following errors:</strong><br/><br/>
          ${errorFields.map(field => `• ${field}`).join('<br/>')}
        </div>`,
        confirmButtonText: 'OK'
      });
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all required fields as touched
    setTouched({
      companyname: true,
      contactperson: true,
      contactno: true,
      email: true,
      gstno: true,
      udyamnumber: true,
      accountno: true,
      bankname: true,
      bankbranchname: true,
      ifsccode: true
    });

    // Validate all fields
    const isFormValid = validateAllFields();

    // Validate addresses
    const isAddressesValid = validateAddresses();

    // Check if required fields are filled
    const isRequiredFieldsValid = checkRequiredFields();

    if (!isFormValid || !isAddressesValid || !isRequiredFieldsValid) {
      // Scroll to the first error field
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setIsLoading(true);

    const supplierData = {
      supplierMultipleAddress: addresses.map(addr => ({
        supplieraddress: addr.supplieraddress || null,
        country: addr.country ? { id: addr.country.value } : null,
        state: addr.state ? { id: addr.state.value } : null,
        city: addr.city ? { id: addr.city.value } : null,
        statecode: addr.statecode || null,
        pincode: addr.pincode || null,
      })),
      companyname: companyname.trim(),
      contactno: contactno.trim() || null,
      gstno: gstno.trim() || null,
      udyamnumber: udyamnumber.trim() || null,
      udyamtype: udyamtype ? udyamtype.value : null,
      email: email.toLowerCase().trim(),
      freight: freight || null,
      packingandforwarding: packingandforwarding || null,
      paymentterm: paymentterm.trim() || null,
      incoterms: incoterms.trim() || null,
      contactperson: contactperson.trim(),
      accountno: accountno.trim() || null,
      bankname: bankname.trim() || null,
      bankbranchname: bankbranchname.trim() || null,
      ifsccode: ifsccode.trim().toUpperCase() || null,
    };

    try {
      await axios.put(`${BASE_URL}supplier/update/${id}`, supplierData, {
        headers: authHeader(),
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Supplier updated successfully!',
      }).then(() => {
        navigate("/supplierindex/1");
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.response?.data || "Failed to update supplier",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="Container">
      <Card className="card">
        <Card.Header className="card-header">
          <h6>EDIT SUPPLIER</h6>
          <Link to="/supplierindex/1">
            <button className="button-70">BACK</button>
          </Link>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md="8">
                <Form.Group controlId="formcompanyname">
                  <Form.Label>Company Name: <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="companyname"
                    value={companyname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.companyname && !!errors.companyname}
                    placeholder="Enter Company Name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.companyname}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formcontactperson">
                  <Form.Label>Contact Person: <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="contactperson"
                    value={contactperson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.contactperson && !!errors.contactperson}
                    placeholder="Enter Contact Person Name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactperson}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formcontactno">
                  <Form.Label>Mobile No.:</Form.Label>
                  <Form.Control
                    type="tel"
                    name="contactno"
                    value={contactno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.contactno && !!errors.contactno}
                    placeholder="Enter 10-digit Mobile Number"
                    maxLength="10"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactno}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: 10-digit mobile number starting with 6-9</Form.Text>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formemail">
                  <Form.Label>Email Id: <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    placeholder="Enter Email Address"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formgstno">
                  <Form.Label>GST No.:</Form.Label>
                  <Form.Control
                    type="text"
                    name="gstno"
                    value={gstno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.gstno && !!errors.gstno}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength="15"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.gstno}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Format: 22AAAAA0000A1Z5 (Optional)</Form.Text>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formudyamnumber">
                  <Form.Label>Udyam Number:</Form.Label>
                  <Form.Control
                    type="text"
                    name="udyamnumber"
                    value={udyamnumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.udyamnumber && !!errors.udyamnumber}
                    placeholder="Enter Udyam Number"
                    maxLength="20"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.udyamnumber}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: Udyam Registration Number</Form.Text>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formudyamtype">
                  <Form.Label>Udyam Type:</Form.Label>
                  <Select
                    value={udyamtype}
                    onChange={setUdyamType}
                    options={udyamTypeOptions}
                    placeholder="Select type"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formpackingandforwarding">
                  <Form.Label>Packing and Forwarding (%):</Form.Label>
                  <Form.Control
                    type="number"
                    value={packingandforwarding}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value >= 0 && value <= 100) {
                        setPackingAndForwarding(value);
                      }
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <Form.Text className="text-muted">Enter value as a percentage (0-100)</Form.Text>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formfreight">
                  <Form.Label>Freight (%):</Form.Label>
                  <Form.Control
                    type="number"
                    value={freight}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value >= 0 && value <= 100) {
                        setFreight(value);
                      }
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <Form.Text className="text-muted">Enter value as a percentage (0-100)</Form.Text>
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formpaymentterm">
                  <Form.Label>Payments Terms:</Form.Label>
                  <Form.Control
                    type="text"
                    name="paymentterm"
                    value={paymentterm}
                    onChange={handleChange}
                    placeholder="Enter Payment Terms"
                  />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group controlId="formincoterms">
                  <Form.Label>Inco terms:</Form.Label>
                  <Form.Control
                    type="text"
                    name="incoterms"
                    value={incoterms}
                    onChange={handleChange}
                    placeholder="EXW, FOB, CIF etc."
                  />
                </Form.Group>
              </Col>

              {/* Bank Details Section */}
              <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                <card className="card" style={{ padding: "10px" }}>
                  <Form.Label>Bank Details</Form.Label>
                  <Row className="rowmulti">
                    <Col md="6">
                      <Form.Group controlId="formbankname">
                        <Form.Label>Bank Name:</Form.Label>
                        <Form.Control
                          type="text"
                          name="bankname"
                          value={bankname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.bankname && !!errors.bankname}
                          placeholder="Enter bank name (Optional)"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.bankname}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group controlId="formaccountno">
                        <Form.Label>Account No.:</Form.Label>
                        <Form.Control
                          type="text"
                          name="accountno"
                          value={accountno}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.accountno && !!errors.accountno}
                          placeholder="Enter account number (Optional)"
                          maxLength="18"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.accountno}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">8-18 digits (Optional)</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group controlId="formbankbranchname">
                        <Form.Label>Branch Name:</Form.Label>
                        <Form.Control
                          type="text"
                          name="bankbranchname"
                          value={bankbranchname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.bankbranchname && !!errors.bankbranchname}
                          placeholder="Enter branch name (Optional)"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.bankbranchname}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group controlId="formifsccode">
                        <Form.Label>IFSC Code:</Form.Label>
                        <Form.Control
                          type="text"
                          name="ifsccode"
                          value={ifsccode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.ifsccode && !!errors.ifsccode}
                          placeholder="SBIN0123456"
                          maxLength="11"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.ifsccode}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">Format: SBIN0123456 (Optional)</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </card>
              </div>
            </Row>

            <br />
            {addresses.map((addr, index) => (
              <div key={index} style={{ marginTop: "10px", marginBottom: "10px" }}>
                <card className="card" style={{ padding: "10px" }}>
                  <Form.Label>Supplier Address {index + 1}</Form.Label>
                  <Row className="rowmulti">
                    <Col md="12">
                      <Form.Group controlId={`formsupplieraddress${index}`}>
                        <Form.Label>Address Line:</Form.Label>
                        <Form.Control
                          type="text"
                          value={addr.supplieraddress}
                          onChange={(e) => handleAddressChange(index, "supplieraddress", e.target.value)}
                          placeholder="Enter address line (Optional)"
                          isInvalid={
                            touched.addresses?.[index]?.supplieraddress &&
                            errors.addresses?.[index]?.supplieraddress
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.addresses?.[index]?.supplieraddress}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group controlId={`formcountry${index}`}>
                        <Form.Label>Country: <span className="text-danger">*</span></Form.Label>
                        <Select
                          value={addr.country}
                          onChange={(selectedOption) => handleCountryChange(index, selectedOption)}
                          options={countryOptions}
                          placeholder="Select country"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                        {touched.addresses?.[index]?.country && errors.addresses?.[index]?.country && (
                          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {errors.addresses[index].country}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group controlId={`formstate${index}`}>
                        <Form.Label>State: <span className="text-danger">*</span></Form.Label>
                        <Select
                          value={addr.state}
                          onChange={(selectedOption) => handleStateChange(index, selectedOption)}
                          options={addr.stateOptions}
                          placeholder="Select state"
                          isDisabled={!addr.country}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                        {touched.addresses?.[index]?.state && errors.addresses?.[index]?.state && (
                          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {errors.addresses[index].state}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group controlId={`formstatecode${index}`}>
                        <Form.Label>State Code:</Form.Label>
                        <Form.Control
                          type="text"
                          value={addr.statecode}
                          readOnly
                          placeholder="Auto-filled"
                        />
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group controlId={`formcity${index}`}>
                        <Form.Label>City: <span className="text-danger">*</span></Form.Label>
                        <Select
                          value={addr.city}
                          onChange={(selectedOption) => handleAddressChange(index, "city", selectedOption)}
                          options={addr.cityOptions}
                          placeholder="Select city"
                          isDisabled={!addr.state}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                        {touched.addresses?.[index]?.city && errors.addresses?.[index]?.city && (
                          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {errors.addresses[index].city}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group controlId={`formpincode${index}`}>
                        <Form.Label>Pincode No.:</Form.Label>
                        <Form.Control
                          type="text"
                          value={addr.pincode}
                          onChange={(e) => handleAddressChange(index, "pincode", e.target.value)}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          isInvalid={
                            touched.addresses?.[index]?.pincode &&
                            errors.addresses?.[index]?.pincode
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.addresses?.[index]?.pincode}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">Optional: 6-digit pincode</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <button style={{ marginTop: "35px", marginLeft: "px" }} type="button" onClick={() => removeAddress(index)} className="btn btn-danger" disabled={addresses.length <= 1}>Remove</button>
                    </Col>
                  </Row>
                  <br />
                </card>
              </div>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                onClick={addAddress}
                className="btn btn-primary"
              >
                + Add Another Address
              </button>

              <button
                type="submit"
                className="btn btn-success"
                disabled={isLoading}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : "Update Supplier"}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SupplierEdit;